import gql from 'graphql-tag';
import { useCallback } from 'react';

import {
  useAdminCaseClusterListViewQuery,
  useDeleteCaseClusterMutation,
} from '@/@generated/codegen/API';
import { useEntitySubscription } from '@/hooks/useEntitySubscription';
import { usePaginationState } from '@/hooks/usePaginationState';
import { PageLoading } from '@/ui/PageLoading';
import { usePrevious } from '@/utils/helpers/react.helper';
import { captureSentry } from '@/utils/helpers/sentryHelper';

import AdminCaseClusterListView from './AdminCaseClusterListView';
import { useAdminCaseClusterListQueryParams } from './hooks/useAdminCaseClusterListQueryParams';

gql`
  query AdminCaseClusterListView(
    $where: CaseClusterWhereInput
    $orderByArgs: OrderByArgs
    $paginationArgs: PaginationArgs
    $meetingsPaginationArgs: PaginationArgs
    $clientsPaginationArgs: PaginationArgs
  ) {
    listCaseClusters(where: $where, orderByArgs: $orderByArgs, paginationArgs: $paginationArgs) {
      edges {
        node {
          ...CaseClusterFields
          ...CaseClusterRelationClients
          ...CaseClusterRelationMeetings
        }
        cursor
      }
      pageInfo {
        hasNextPage
        endCursor
        hasPreviousPage
        startCursor
      }
      totalCount
    }
  }
`;

const PAGE_SIZE = 15;
export const caseClusterTablePrefix = 'caseClusters';

export default function AdminCaseClusterListClient() {
  const { orderByArgs, paginationArgs, where } = useAdminCaseClusterListQueryParams(
    PAGE_SIZE,
    caseClusterTablePrefix
  );
  const caseClusters = useAdminCaseClusterListViewQuery({
    variables: {
      paginationArgs,
      orderByArgs,
      where,
    },
  });
  const previousData = usePrevious(caseClusters.data);
  const pagination = usePaginationState(PAGE_SIZE, caseClusterTablePrefix);
  const [mutationDeleteCaseCluster] = useDeleteCaseClusterMutation();

  useEntitySubscription('CaseCluster', {
    onCreate: {
      variables: {
        where,
      },
      onData: ({ data }) => {
        const obj = data.data?.onCreateCaseCluster;
        if (!obj) {
          captureSentry({
            title: 'AdminCaseClusterListClient | onData | onCreateCaseCluster | obj is undefined',
            detail: {},
          });
          return;
        }
        void caseClusters.refetch();
      },
    },
    onDelete: {
      variables: {},
      onData: ({ data }) => {
        const obj = data.data?.onDeleteCaseCluster;
        if (!obj) {
          captureSentry({
            title: 'AdminCaseClusterListClient | onData | onDeleteCaseCluster | obj is undefined',
            detail: {},
          });
          return;
        }
        void caseClusters.refetch();
      },
    },
  });

  const handleDeleteCaseCluster = useCallback(
    (caseClusterID: string) => {
      void mutationDeleteCaseCluster({
        variables: {
          where: {
            id: caseClusterID,
          },
        },
      });
    },
    [mutationDeleteCaseCluster]
  );

  if (!caseClusters.data && !previousData) {
    return <PageLoading />;
  }
  return (
    <>
      <AdminCaseClusterListView
        data={caseClusters.data || previousData!}
        pagination={pagination}
        handleDeleteCaseCluster={handleDeleteCaseCluster}
        tablePrefix={caseClusterTablePrefix}
      />
    </>
  );
}
